import { useState } from "react";
import { Row, Col, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import { Badge, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { simpleDate, simpleTime } from "../../../utils/displayDate";
import { customTableStyles } from "../styles/table";
import { numberWithCommas } from "../../../utils/math";
import SimpleModal from "../../../components/common/SimpleModal";
import UpdateAccountForm from "./form/UpdateAccountForm";
import UpdateMemberLevelForm from "./form/UpdateMemberLevelForm";
import {
  setIdentity,
  setResetPassword,
  setResetWithdrawPassword,
  setUpdateAccount,
  setUpdateMemberLevel,
  toggleIdentityModal,
  toggleLevelModal,
  togglePasswordModal,
  toggleUpdateAccountModal,
  toggleWithdrawPasswordModal,
} from "../../management/redux/managementSlice";
import {
  ChevronIcon,
  EditIcon,
  PasswordIcon,
} from "../../../constants/icon_path";
import LoadingTable from "../../../components/common/LoadingTable";
import ResetPasswordForm from "./form/ResetPasswordForm";
import ApproveIdentityForm from "./form/ApproveIdentityForm";
import ResetWithdrawPasswordForm from "./form/ResetWithdrawPasswordForm";

const MemberTable = ({ data, loading }) => {
  const dispatch = useDispatch();
  const {
    isOpenUpdateAccountModal,
    isOpenLevelModal,
    isOpenPasswordModal,
    isOpenIdentityModal,
    isOpenWithdrawPasswordModal,
  } = useSelector((state) => state.management);

  const handleEdit = (data) => {
    dispatch(setUpdateAccount(data));
    dispatch(toggleUpdateAccountModal());
  };

  const handleUpdateMemberLevel = (data) => {
    dispatch(
      setUpdateMemberLevel({ member: data.id, level: data.memberLevel })
    );
    dispatch(toggleLevelModal());
  };

  const handleResetPassword = (data) => {
    dispatch(setResetPassword({ member: data.id, username: data.username }));
    dispatch(togglePasswordModal());
  };

  const handleResetWithdrawPassword = (data) => {
    dispatch(
      setResetWithdrawPassword({ member: data.id, username: data.username })
    );
    dispatch(toggleWithdrawPasswordModal());
  };

  const handleShowIdentity = (user) => {
    if (user.isVerified || user.isPeningIdentity) {
      dispatch(
        setIdentity({
          id: user.id,
          cardName: user.identity.cardName,
          cardNo: user.identity.cardNo,
          cardImageUrl: user.identity.cardImageUrl,
          isVerified: user.isVerified,
        })
      );
      dispatch(toggleIdentityModal());
    }
  };

  const handleCloseUpdateModal = () => dispatch(toggleUpdateAccountModal());
  const handleCloseModalLevel = () => dispatch(toggleLevelModal());
  const handleCloseModalPassword = () => dispatch(togglePasswordModal());
  const handleCloseModalWithdrawPassword = () =>
    dispatch(toggleWithdrawPasswordModal());
  const handleToggleIdentityModal = () => dispatch(toggleIdentityModal());

  const [searchValue, setSearchValue] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  // ** Function to handle filter
  const handleFilter = (e) => {
    const value = e.target.value;
    let updatedData = [];
    setSearchValue(value);

    if (value.length) {
      updatedData = data?.filter((item) => {
        const startsWith =
          item.memberId.toString().startsWith(value.toString()) ||
          item.username.toString().toLowerCase().startsWith(value.toString()) ||
          item.nickname.toString().toLowerCase().startsWith(value.toString());
        const includes =
          item.memberId.toString().includes(value.toString()) ||
          item.username.toString().includes(value.toString()) ||
          item.nickname.toString().includes(value.toString());

        if (startsWith) {
          return startsWith;
        } else if (!startsWith && includes) {
          return includes;
        } else return null;
      });

      setFilteredData(updatedData);
      setSearchValue(value);
    }
  };

  const renderMemberLevel = (level) => {
    switch (level) {
      case 1:
        return (
          <Badge bg="info">
            <span style={{ fontSize: "12px" }}>Gold</span>
          </Badge>
        );
      case 2:
        return (
          <Badge bg="primary">
            <span style={{ fontSize: "12px" }}>VIP</span>
          </Badge>
        );
      default:
        break;
    }
  };

  const columns = [
    {
      name: "ລະຫັດລູກຄ້າ",
      selector: (row) => row.memberId,
      center: true,
      sortable: true,
      className: "notolao",
    },
    {
      name: "Username",
      selector: (row) => row.username,
      center: true,
      sortable: true,
      className: "notolao",
    },
    {
      name: "ຊື່ລູກຄ້າ",
      selector: (row) => row.nickname,
      center: true,
      sortable: true,
    },
    {
      name: "ເລກບັນຊີ",
      selector: (row) => row.account?.accountNo,
      sortable: true,
      center: true,
    },

    {
      name: "ຍອດເງິນ",
      selector: (row) => numberWithCommas(row.balance),
      sortable: true,
      center: true,
    },

    {
      name: "ວັນທີ່ສະໝັກ",
      sortable: true,
      center: true,
      cell: (row) => (
        <span className="d-flex flex-column">
          <span>{simpleDate(row.createdAt)}</span>
          <span>{simpleTime(row.createdAt)}</span>
        </span>
      ),
    },
    {
      name: "ແກ້ໄຂເລກບັນຊີ",
      minWidth: "150px",
      sortable: true,
      center: true,
      cell: (row) => (
        <Button
          className="btn-xs"
          variant="success"
          onClick={() =>
            handleEdit({
              memberId: row.id,
              accountNo: row?.account?.accountNo,
              accountName: row?.account?.accountName,
              bankName: row?.account?.bankName,
              updatedAccount: row?.updatedAccount,
              crypto: row?.crypto,
              accountType: row?.accountType,
            })
          }
        >
          <EditIcon size={16} /> ແກ້ໄຂເລກບັນຊີ
        </Button>
      ),
    },
    {
      name: "ລະດັບສະມາຊິກ",
      minWidth: "150px",
      sortable: true,
      center: true,
      cell: (row) => (
        <>
          <div className="m-2">{renderMemberLevel(row.memberLevel)}</div>
          <EditIcon
            size={20}
            onClick={() => handleUpdateMemberLevel(row)}
            color="green"
          />
        </>
      ),
    },
    {
      name: "ລະຫັດຜ່ານ",
      minWidth: "60px",
      sortable: true,
      center: true,
      cell: (row) => (
        <>
          <PasswordIcon
            size={20}
            onClick={() => handleResetPassword(row)}
            color="green"
          />
        </>
      ),
    },
    {
      name: "ລະຫັດຖອນເງິນ",
      minWidth: "150px",
      sortable: true,
      center: true,
      cell: (row) => (
        <>
          <PasswordIcon
            size={20}
            onClick={() => handleResetWithdrawPassword(row)}
            color="green"
          />
        </>
      ),
    },
    {
      name: "ຢືນຢັນຕົວຕົນ",
      minWidth: "150px",
      sortable: true,
      center: true,
      cell: (row) => (
        <>
          <Button
            className="btn-xs"
            variant={
              row.isPeningIdentity
                ? "warning"
                : row.isVerified
                ? "success"
                : "danger"
            }
            onClick={() => handleShowIdentity(row)}
          >
            {row.isPeningIdentity
              ? "ຢືນຢັນ"
              : row.isVerified
              ? "ຢືນຢັນແລ້ວ"
              : "ຍັງບໍຢືນຢັນ"}
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <SimpleModal
        show={isOpenIdentityModal}
        handleClose={handleToggleIdentityModal}
        title="ຢືນຢັນຕົວຕົນ"
        size="md"
      >
        <ApproveIdentityForm handleClose={handleToggleIdentityModal} />
      </SimpleModal>
      <SimpleModal
        show={isOpenPasswordModal}
        handleClose={handleCloseModalPassword}
        title="ຕັ້ງລະຫັດຜ່ານໃໝ່"
        size="md"
      >
        <ResetPasswordForm handleClose={handleCloseModalPassword} />
      </SimpleModal>
      <SimpleModal
        show={isOpenWithdrawPasswordModal}
        handleClose={handleCloseModalWithdrawPassword}
        title="ຕັ້ງລະຫັດຖອນເງິນໃໝ່"
        size="md"
      >
        <ResetWithdrawPasswordForm
          handleClose={handleCloseModalWithdrawPassword}
        />
      </SimpleModal>
      <SimpleModal
        show={isOpenUpdateAccountModal}
        handleClose={handleCloseUpdateModal}
        title="ແກ້ໄຂເລກບັນຊີ"
        size="lg"
      >
        <UpdateAccountForm handleClose={handleCloseUpdateModal} />
      </SimpleModal>
      <SimpleModal
        show={isOpenLevelModal}
        handleClose={handleCloseModalLevel}
        title="ແກ້ໄຂລະດັບສະມາຂິກ"
        size="sm"
      >
        <UpdateMemberLevelForm handleClose={handleCloseModalLevel} />
      </SimpleModal>
      <Row className="mb-2 d-flex">
        <Col md="6">
          <Input
            type="text"
            value={searchValue}
            onChange={handleFilter}
            placeholder="ຄົ້ນຫາ"
            size="sm"
          />
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <DataTable
            responsive
            noHeader
            pagination
            data={searchValue.length ? filteredData : data && data}
            columns={columns}
            className="react-dataTable"
            sortIcon={<ChevronIcon size={10} />}
            filteredData="true"
            customStyles={customTableStyles}
            paginationRowsPerPageOptions={[50, 100, 150, 200]}
            progressPending={loading}
            paginationPerPage={50}
            progressComponent={<LoadingTable />}
          />
        </Col>
      </Row>
    </>
  );
};

export default MemberTable;
